import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  resource,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';

import { ZarcApiKeyType } from '#backend/common/zarc-types';
import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import {
  UpdateZoneamentoDialogComponent,
  type UpdateZoneamentoExecutionMode,
} from './update-zoneamento-dialog/update-zoneamento-dialog.component';

const ZARC_API_PDF_URL =
  'https://www.agroapi.cnptia.embrapa.br/portal/assets/docs/agroapi-primeiros-passos.pdf';

@Component({
  selector: 'app-zarc-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatDialogModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    MatSelectModule,
    MatTooltipModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    provideLuxonDateAdapter(),
  ],
  templateUrl: './zarc-settings.component.html',
  styleUrl: './zarc-settings.component.scss',
})
export class ZarcSettingsComponent {
  private readonly orpc = inject(SmartOrpcClientService);
  private readonly snackBar = inject(SnackbarService);
  private readonly dialog = inject(MatDialog);

  readonly apiKeyTypes = [
    { value: ZarcApiKeyType.FREE, label: 'Gratuita' },
    { value: ZarcApiKeyType.PAID, label: 'Paga' },
  ];

  readonly apiPdfUrl = ZARC_API_PDF_URL;

  readonly isSaving = signal(false);
  readonly showApiKey = signal(false);
  readonly isRunningUpdate = signal(false);
  readonly isSavingCultures = signal(false);

  /** Mapa de cultureId → valor editado no input (antes de salvar) */
  readonly editedSubtractionDays = new Map<number, number | null>();

  readonly form = new FormGroup({
    apiKey: new FormControl<string | null>(null),
    apiKeyType: new FormControl<ZarcApiKeyType | null>(null, [
      Validators.required,
    ]),
    apiKeyGeneratedAt: new FormControl<DateTime | null>(null, [
      Validators.required,
    ]),
  });

  /** Load current ZARC settings from backend */
  readonly settings = resource({
    loader: async ({ abortSignal }) => {
      const { data } = await this.orpc.safeClient.admin.zarc.getSettings(
        undefined,
        { signal: abortSignal },
      );
      return data;
    },
  });

  /** Culturas ativas para edição dos dias de subtração (RF006-v2) */
  readonly cultures = resource({
    loader: async ({ abortSignal }) => {
      const { data } = await this.orpc.safeClient.monitoring.cultures.list(
        {},
        { signal: abortSignal },
      );
      // Sincroniza o mapa com os valores persistidos no banco (sempre que recarregar)
      for (const c of data ?? []) {
        this.editedSubtractionDays.set(c.id, c.subtractionDays ?? null);
      }
      return data;
    },
  });

  /** Expiration date = generatedAt + 30 days — atualiza em tempo real após qualquer alteração no datepicker */
  private readonly _generatedAtSignal = toSignal(
    this.form.controls.apiKeyGeneratedAt.valueChanges,
    { initialValue: this.form.controls.apiKeyGeneratedAt.value },
  );

  readonly apiKeyExpirationAt = computed(() => {
    const g = this._generatedAtSignal();
    if (!g) return null;
    return g.plus({ days: 30 });
  });

  constructor() {
    // Populate form when settings are loaded
    effect(() => {
      const s = this.settings.value();
      if (!s) return;

      this.form.patchValue({
        apiKey: s.apiKey,
        apiKeyType: s.apiKeyType,
        apiKeyGeneratedAt: s.apiKeyGeneratedAt
          ? DateTime.fromISO(s.apiKeyGeneratedAt)
          : null,
      });
    });
  }

  get lastRunAt(): string {
    const s = this.settings.value();
    if (!s?.lastRunAt) return '—';
    return DateTime.fromISO(s.lastRunAt)
      .setLocale('pt-BR')
      .toLocaleString(DateTime.DATETIME_SHORT);
  }

  get lastRunRequestCount(): number {
    return this.settings.value()?.lastRunRequestCount ?? 0;
  }

  get availableRequestBalance(): number {
    return this.settings.value()?.availableRequestBalance ?? 0;
  }

  async onSave(): Promise<void> {
    if (this.form.invalid || this.isSaving()) return;

    const { apiKey, apiKeyType, apiKeyGeneratedAt } = this.form.getRawValue();

    if (!apiKeyType || !apiKeyGeneratedAt) return;

    this.isSaving.set(true);
    try {
      await this.orpc.safeClient.admin.zarc.updateSettings({
        apiKey: apiKey ?? null,
        apiKeyType,
        apiKeyGeneratedAt: apiKeyGeneratedAt.toISODate()!,
        // RN004: calculado no frontend e enviado para persistir no banco
        apiKeyExpirationAt: apiKeyGeneratedAt.plus({ days: 30 }).toISODate()!,
      });

      this.snackBar.alert('Configurações do ZARC salvas com sucesso.');
      this.settings.reload();
    } finally {
      this.isSaving.set(false);
    }
  }

  getFieldError(controlName: keyof typeof this.form.controls): string | null {
    const control = this.form.controls[controlName];
    if (!control.touched || control.valid) return null;
    if (control.hasError('required')) return 'Campo obrigatório.';
    return 'Valor inválido.';
  }

  /** RF006-v2 — Valida e salva os dias de subtração de todas as culturas em paralelo. */
  async onSaveAllCultureSubtractionDays(): Promise<void> {
    if (this.isSavingCultures()) return;

    // Validar todos os valores antes de enviar qualquer requisição
    for (const [, rawValue] of this.editedSubtractionDays) {
      if (rawValue === null || rawValue === undefined) continue;
      const val = Number(rawValue);
      if (!Number.isInteger(val) || val < 0 || val > 20) {
        this.snackBar.alert(
          'Valor inválido em uma das culturas. Informe inteiros entre 0 e 20.',
        );
        return;
      }
    }

    this.isSavingCultures.set(true);
    try {
      for (const [cultureId, rawValue] of this.editedSubtractionDays) {
        const subtractionDays =
          rawValue === null || rawValue === undefined ? null : Number(rawValue);
        const { error } =
          await this.orpc.safeClient.admin.zarc.updateCultureSubtractionDays({
            cultureId,
            subtractionDays,
          });

        if (error) return;
      }

      this.snackBar.alert('Dias de subtração salvos com sucesso.');
      this.cultures.reload();
    } finally {
      this.isSavingCultures.set(false);
    }
  }

  /** RF004 — Opens confirmation dialog and triggers ZARC base update in background. */
  async onRunUpdate(): Promise<void> {
    if (this.isRunningUpdate()) return;

    const mode = await firstValueFrom(
      this.dialog
        .open<
          UpdateZoneamentoDialogComponent,
          void,
          UpdateZoneamentoExecutionMode
        >(UpdateZoneamentoDialogComponent, {
          width: '560px',
          disableClose: false,
        })
        .afterClosed(),
    );

    if (mode === undefined) return; // user cancelled

    this.isRunningUpdate.set(true);
    try {
      // Chamada retorna imediatamente com { queued: true }
      // O loop de API roda em background no servidor
      await this.orpc.safeClient.admin.zarc.runZoneamentoUpdate({
        executionMode: mode,
      });

      this.snackBar.alert(
        'Atualização iniciada em segundo plano. Recarregue a página em alguns minutos para ver o resultado.',
      );
    } finally {
      this.isRunningUpdate.set(false);
    }
  }
}
